<template>
    <!-- Informations -->
    <div class="tab-wrapper" :class="{'mobile': getMobileAppState}" v-if="informationsList.length">
        <div v-for="(lineGroup, index) in parsedAccountDetails" :key="`lineGroup${lineGroup.id}`" :class="getMobileAppState ? 'mobile' : ''">
            <!-- Title -->
            <divider-generic
            dividerSize="medium"
            :icon="lineGroup.icon"
            :title="lineGroup.label"
            :applyBorder="index !== 0"/>
            <div v-for="line in lineGroup.lines" :key="`lineGroupLine${line,id}`">
                <!-- Generic Block -->
                <text-block-generic
                v-if="line.fieldName !== 'account_description'"
                style="margin-bottom: 16px;"
                :params="line"
                :mask="line.mask"
                :icon="line.icon"
                :colorIconPreset="line.colorIconPreset"
                applyEllipsis/>
                <!-- Editable Block -->
                <text-block-editable
                v-else
                :params="accountDescriptionParams"
                :requestPayload="accountDescriptionPayload"
                @updateContent="temporaryAccountDescription = $event"
                @requestSuccessful="accountDescription = $event.description"/>
            </div>
        </div>
    </div>
    <!-- Empty State -->
    <empty-state-generic
    v-else
    class="empty-state"
    illustration="monocrome/empty-state-credit"
    :title="t('credits.myCredits.informations.emptyStates.title')"
    :description="t('credits.myCredits.informations.emptyStates.description')"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import TextBlockEditable from '@/components/generic/textBlocks/Editable'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
/* Vue */
import { ref, computed } from 'vue'
/* Assets & Helpers */
import { parseLineGroups } from '@/assets/scripts/parsers/genericDetail'
import { getDate } from '@/assets/scripts/dateBuilders'
import { amountFormat } from '@/assets/scripts/amounts'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapGetters */
const { getMobileAppState } = mapGetters()

/* Props received:
@informationsList {Array}
@creditAccountExtraInfo {Object}
@accountCurrency {String}
@selectedAccount {String}
*/
const props = defineProps({
    informationsList: Array,
    creditAccountExtraInfo: Object,
    currency: String,
    selectedAccount: String
})

/* Data */
const accountDescription = ref(null)
const temporaryAccountDescription = ref(null)

const parsedAccountDetails = computed(() => {
    if (!props.informationsList) return
	return parseLineGroups(props.informationsList).map(group => {
        /*
        * Credit accounts don't have nba/iban values,
        * so we will be changing this group with financial plan info
        */
        if (group.id === 4) {
            group.icon = 'document-people'
            group.label = t('credits.myCredits.informations.financialPlanBlockTitle')
            group.lines = [
                {
                    label: t('credits.myCredits.informations.initDateLabel'),
                    content: getDate(props.creditAccountExtraInfo.openDate),
                    classes: 'capitalize'
                }, {
                    label: t('credits.myCredits.informations.endDateLabel'),
                    content: getDate(props.creditAccountExtraInfo.maturityDate),
                    classes: 'capitalize'
                }, {
                    label: t('credits.myCredits.informations.numberOfPaymentInInstallmentsLabel'),
                    content: props.creditAccountExtraInfo.numberOfPaymentInInstallments
                }, {
                    label: t('credits.myCredits.informations.loanInitialValueLabel'),
                    content: amountFormat(Math.abs(props.creditAccountExtraInfo.loanInitialValue), props.currency)
                }, {
                    label: t('credits.myCredits.informations.loanLeftToPayLabel'),
                    content: amountFormat(Math.abs(props.creditAccountExtraInfo.loanLeftToPay), props.currency)
                }
            ]
        }
        return group
    })
})

const accountDescriptionPayload = computed(() => {
	return {
		accountId: props.selectedAccount,
		data: {
			description: temporaryAccountDescription.value
		}
	}
})

const accountDescriptionParams = computed(() => {
    const detailLineGroup = parsedAccountDetails.value.find(line => line.id === 2).lines
    const descriptionLine = detailLineGroup.find(line => line.fieldName === 'account_description')
    const description = !accountDescription.value ? descriptionLine.lineValue : accountDescription.value

	return {
		label: descriptionLine.label,
		title: t('credits.myCredits.informations.editDescription'),
		content: description,
		maxLength: 25,
        showCounter: true,
		editAction: `${[moduleMapper.CREDITS_MY_CREDITS]}/updateAccountDescription`
	}
})
</script>

<style lang="scss" scoped>
.tab-wrapper {
    width: 508px;

    .divider-generic-wrapper {
        margin-bottom: 24px;
    }

    .text-blocks-wrapper {
        margin-bottom: 24px;
    }

    &.mobile {
        padding: 0 16px 72px 16px;
    }
}
</style>
